<!--
 * @Description: 模板选择弹窗组件（图片特效/视频特效）
 * @FilePath: /weryai-web/components/chatCreation/TemplateSelectModal.vue
-->
<template>
    <Teleport to="body">
        <Transition name="modal-fade">
            <div
                v-if="visible"
                class="template-modal-overlay"
                @click.self="handleClose"
            >
                <div class="template-modal">
                <!-- 头部 -->
                <div class="modal-header">
                    <h3 class="modal-title">
                        {{ type === 'IMAGE' ? t('templateModal.imageEffects') : t('templateModal.videoEffects') }}
                    </h3>
                    <button
                        type="button"
                        class="close-btn"
                        @click="handleClose"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <!-- 模板网格 -->
                <div
                    ref="scrollContainerRef"
                    class="modal-body"
                    @scroll="handleScroll"
                >
                    <!-- 加载中 -->
                    <div v-if="loading && templateList.length === 0" class="loading-container">
                        <ClientOnly>
                            <l-dot-spinner size="40" speed="0.9" color="var(--el-menu-active-color)"></l-dot-spinner>
                        </ClientOnly>
                    </div>

                    <!-- 模板列表 -->
                    <div v-else class="template-grid">
                        <div
                            v-for="template in templateList"
                            :key="template.id"
                            class="template-item"
                            :class="{ 'is-selected': selectedId === template.id }"
                            @click="handleSelect(template)"
                        >
                            <div 
                                class="template-image-wrapper"
                                @mouseenter="handleCardMediaEnter"
                                @mouseleave="handleCardMediaLeave"
                            >
                                <!-- 视频模板：先显示封面图片 -->
                                <img
                                    v-if="type === 'VIDEO' && template.cover_url && !videoLoadedMap[template.id]"
                                    :src="template.cover_url"
                                    :alt="template.name"
                                    class="template-image"
                                    loading="lazy"
                                />
                                <!-- 视频模板：视频加载完成后显示 -->
                                <video
                                    v-if="type === 'VIDEO' && template.video_urls && template.video_urls.length > 0"
                                    v-show="videoLoadedMap[template.id]"
                                    :src="template.video_urls[0]"
                                    class="template-image"
                                    :poster="template.cover_url"
                                    loop
                                    muted
                                    playsinline
                                    webkit-playsinline
                                    preload="metadata"
                                    @canplay="handleVideoLoaded(template.id)"
                                />
                                <!-- 视频模板：无视频时只显示封面 -->
                                <img
                                    v-if="type === 'VIDEO' && template.cover_url && (!template.video_urls || template.video_urls.length === 0)"
                                    :src="template.cover_url"
                                    :alt="template.name"
                                    class="template-image"
                                    loading="lazy"
                                />
                                <!-- 图片模板：只显示图片 -->
                                <img
                                    v-if="type === 'IMAGE'"
                                    :src="template.cover_url"
                                    :alt="template.name"
                                    class="template-image"
                                    loading="lazy"
                                />
                            </div>
                            <div class="template-name-wrapper">
                                <span class="template-name">{{ template.name }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- 加载更多 -->
                    <div v-if="loading && templateList.length > 0" class="loading-more">
                        <ClientOnly>
                            <l-dot-spinner size="30" speed="0.9" color="var(--el-menu-active-color)"></l-dot-spinner>
                        </ClientOnly>
                    </div>

                    <!-- 没有更多 -->
                    <div v-if="!loading && !hasMore && templateList.length > 0" class="no-more">
                        {{ t('templateModal.noMore') }}
                    </div>

                    <!-- 空状态 -->
                    <div v-if="!loading && templateList.length === 0" class="empty-state">
                        {{ t('templateModal.noData') }}
                    </div>
                </div>

                <!-- 底部按钮 -->
                <div class="modal-footer">
                    <button
                        type="button"
                        class="btn btn-cancel"
                        @click="handleClose"
                    >
                        {{ t('templateModal.cancel') }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-confirm"
                        :disabled="!selectedId || fetchingDetail"
                        @click="handleConfirm"
                    >
                        <ClientOnly>
                            <l-dot-spinner
                                v-if="fetchingDetail"
                                size="18"
                                speed="0.9"
                                color="#000"
                            ></l-dot-spinner>
                            <span v-else>{{ t('templateModal.save') }}</span>
                        </ClientOnly>
                    </button>
                </div>
            </div>
        </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { 
    getEffectTemplatePageApi, 
    getEffectTemplateInfoApi,
    type EffectTemplateItem,
    type EffectTemplateDetail,
    type EffectTemplateConfig
} from '~/api/effects'

// 确认选择时返回的完整数据
export interface TemplateConfirmData {
    template: EffectTemplateItem
    detail: EffectTemplateDetail | null
    config: EffectTemplateConfig | null
}

interface Props {
    visible: boolean
    type?: 'IMAGE' | 'VIDEO'
    modelValue?: number | null
}

interface Emits {
    (e: 'update:visible', value: boolean): void
    (e: 'update:modelValue', value: number | null): void
    (e: 'confirm', data: TemplateConfirmData): void
}

const props = withDefaults(defineProps<Props>(), {
    type: 'IMAGE',
    modelValue: null,
})

const emit = defineEmits<Emits>()

const { t, locale } = useI18n()

// 滚动容器引用
const scrollContainerRef = ref<HTMLElement | null>(null)

// 模板列表数据
const templateList = ref<EffectTemplateItem[]>([])

// 当前选中的模板 ID
const selectedId = ref<number | null>(props.modelValue)

// 当前选中的模板对象
const selectedTemplate = ref<EffectTemplateItem | null>(null)

// 分页状态
const pageNum = ref(1)
const pageSize = 12
const total = ref(0)
const loading = ref(false)

// 是否还有更多
const hasMore = computed(() => {
    return templateList.value.length < total.value
})

// ========== 视频播放控制 ==========
// 视频加载状态记录
const videoLoadedMap = ref<Record<number, boolean>>({})

// 视频加载完成回调
const handleVideoLoaded = (templateId: number) => {
    videoLoadedMap.value[templateId] = true
}

// 视频暂停定时器（避免抖动）
const pauseTimers = new WeakMap<HTMLVideoElement, ReturnType<typeof setTimeout>>()

// 视频预热
const warmUpOnce = (video: HTMLVideoElement) => {
    if ((video.dataset as any).weryWarm === '1') return
    ;(video.dataset as any).weryWarm = '1'
    try {
        video.preload = 'auto'
    } catch {
        // ignore
    }
}

// 鼠标移入：播放视频
const handleCardMediaEnter = async (event: MouseEvent) => {
    if (props.type !== 'VIDEO') return
    
    const el = event.currentTarget as HTMLElement | null
    const video = el?.querySelector('video') as HTMLVideoElement | null
    if (!video) return

    const existing = pauseTimers.get(video)
    if (existing) {
        clearTimeout(existing)
        pauseTimers.delete(video)
    }

    warmUpOnce(video)
    if (!video.paused) return
    try {
        await video.play()
    } catch (error: any) {
        if (error?.name !== 'AbortError') {
            console.warn('视频播放失败:', error)
        }
    }
}

// 鼠标移出：暂停视频（带延迟避免抖动）
const handleCardMediaLeave = (event: MouseEvent) => {
    if (props.type !== 'VIDEO') return
    
    const el = event.currentTarget as HTMLElement | null
    const video = el?.querySelector('video') as HTMLVideoElement | null
    if (!video) return

    const existing = pauseTimers.get(video)
    if (existing) clearTimeout(existing)

    const timer = setTimeout(() => {
        pauseTimers.delete(video)
        if (!video.paused) video.pause()
    }, 250)
    pauseTimers.set(video, timer)
}

// 获取模板列表
const fetchTemplates = async (isLoadMore = false) => {
    if (loading.value) return

    loading.value = true
    try {
        const res = await getEffectTemplatePageApi({
            type: props.type,
            pageNum: pageNum.value,
            pageSize,
            lang: locale.value,
        })

        if (res.status === 200 && res.data) {
            if (isLoadMore) {
                templateList.value = [...templateList.value, ...res.data.content]
            } else {
                templateList.value = res.data.content
            }
            total.value = res.data.total
        }
    } catch (error) {
        console.error('获取模板列表失败:', error)
    } finally {
        loading.value = false
    }
}

// 加载更多
const loadMore = () => {
    if (!hasMore.value || loading.value) return
    pageNum.value++
    fetchTemplates(true)
}

// 滚动加载
const handleScroll = (e: Event) => {
    const target = e.target as HTMLElement
    const { scrollTop, scrollHeight, clientHeight } = target
    // 距离底部 100px 时加载更多
    if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMore()
    }
}

// 监听 visible 变化
watch(
    () => props.visible,
    (newVal) => {
        if (newVal) {
            // 打开弹窗时重置状态并加载数据
            pageNum.value = 1
            templateList.value = []
            selectedId.value = props.modelValue
            if (props.modelValue) {
                selectedTemplate.value = templateList.value.find(t => t.id === props.modelValue) || null
            } else {
                selectedTemplate.value = null
            }
            fetchTemplates()
        }
    }
)

// 监听 type 变化
watch(
    () => props.type,
    () => {
        if (props.visible) {
            pageNum.value = 1
            templateList.value = []
            fetchTemplates()
        }
    }
)

// 监听 modelValue 变化
watch(
    () => props.modelValue,
    (newVal) => {
        selectedId.value = newVal
        if (newVal) {
            selectedTemplate.value = templateList.value.find(t => t.id === newVal) || null
        } else {
            selectedTemplate.value = null
        }
    }
)

// 选择模板
const handleSelect = (template: EffectTemplateItem) => {
    selectedId.value = template.id
    selectedTemplate.value = template
}

// 关闭弹窗
const handleClose = () => {
    emit('update:visible', false)
}

// 正在获取详情
const fetchingDetail = ref(false)

// 确认选择
const handleConfirm = async () => {
    if (!selectedId.value || !selectedTemplate.value) return
    
    fetchingDetail.value = true
    try {
        // 获取模板详情（包含 configs）
        const detailRes = await getEffectTemplateInfoApi(
            selectedTemplate.value.path_url,
            locale.value
        )
        
        let detail: EffectTemplateDetail | null = null
        let config: EffectTemplateConfig | null = null
        
        if (detailRes.status === 200 && detailRes.data) {
            detail = detailRes.data
            // 默认使用第一个配置
            if (detail.configs && detail.configs.length > 0) {
                config = detail.configs[0] ?? null
            }
        }
        
        emit('update:modelValue', selectedId.value)
        emit('confirm', {
            template: selectedTemplate.value,
            detail,
            config
        })
        handleClose()
    } catch (error) {
        console.error('获取模板详情失败:', error)
        // 即使获取详情失败，也返回基本信息
        emit('update:modelValue', selectedId.value)
        emit('confirm', {
            template: selectedTemplate.value,
            detail: null,
            config: null
        })
        handleClose()
    } finally {
        fetchingDetail.value = false
    }
}
</script>

<style scoped lang="less">
.template-modal-overlay {
    position: fixed;
    z-index: 3000;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.7);
}

.template-modal {
    position: relative;
    width: 900px;
    max-width: 90vw;
    height: 85vh;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    border-radius: 24px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: #1c1c20;
    overflow: hidden;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 30px 20px;
}

.modal-title {
    font-size: 18px;
    font-weight: 600;
    color: #fff;
    margin: 0;
}

.close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 8px;
    color: #fff;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
}

.modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 0 30px 20px;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 3px;

        &:hover {
            background: rgba(255, 255, 255, 0.3);
        }
    }
}

.template-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;

    @media (max-width: 800px) {
        grid-template-columns: repeat(3, 1fr);
        gap: 16px;
    }

    @media (max-width: 600px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
}

.template-item {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid transparent;

    .template-image-wrapper {
        position: relative;
        width: 100%;
        aspect-ratio: 1 / 1;
        overflow: hidden;
        background: #2b2b30;
    }

    .template-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .template-name-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 12px 8px;
        background: #2b2b30;
        min-height: 44px;
    }

    .template-name {
        font-size: 13px;
        color: #fff;
        text-align: center;
        line-height: 1.4;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &:hover {
        border-color: rgba(255, 255, 255, 0.3);
    }

    &.is-selected {
        border-color: #fff;
    }
}

.modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px 30px 30px;
}

.btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 80px;
    height: 40px;
    padding: 0 24px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.btn-cancel {
    background: #2b2b30;
    color: #adadad;
    border: 1px solid rgba(255, 255, 255, 0.1);

    &:hover:not(:disabled) {
        background: #3a3a40;
        color: #fff;
    }
}

.btn-confirm {
    background: #99f768;
    color: #000;

    &:hover:not(:disabled) {
        background: #aaf87a;
    }
}

// 加载状态
.loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 400px;
}

.loading-more {
    display: flex;
    justify-content: center;
    padding: 20px 0;
}

.no-more {
    text-align: center;
    padding: 20px 0;
    font-size: 13px;
    color: #666;
}

.empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 80px 0;
    font-size: 14px;
    color: #8a8a90;
}

// 过渡动画
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.3s ease;

    .template-modal {
        transition: transform 0.3s ease;
    }
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;

    .template-modal {
        transform: scale(0.95);
    }
}
</style>
